import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI, formatCurrency } from '../services/api';
import { Search, FileText, Users, Receipt, Wallet, X } from 'lucide-react';

const ICONS = { invoice: FileText, party: Users, receipt: Receipt, advance: Wallet };
const COLORS = {
  invoice: 'bg-blue-50 text-blue-700', party: 'bg-emerald-50 text-emerald-700',
  receipt: 'bg-orange-50 text-orange-700', advance: 'bg-purple-50 text-purple-700',
};

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      searchAPI.search(q).then(r => { setResults(r.data.results || []); setOpen(true); })
        .catch(() => {}).finally(() => setLoading(false));
    }, 300);
  };

  const handleSelect = (r) => {
    setOpen(false); setQuery('');
    if (r.url) navigate(r.url);
  };

  return (
    <div ref={ref} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text" value={query} onChange={e => doSearch(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search invoices, parties, receipts..."
          className="w-full pl-9 pr-8 py-2 text-sm bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-md focus:bg-white dark:focus:bg-slate-600 focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-500 focus:border-slate-300 dark:focus:border-slate-500 text-slate-900 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 transition"
          data-testid="global-search-input" />
        {query && (
          <button onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded" data-testid="search-clear">
            <X className="w-3.5 h-3.5 text-slate-400" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-80 overflow-y-auto" data-testid="search-results">
          {loading ? (
            <div className="flex items-center justify-center py-4"><div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" /></div>
          ) : results.length > 0 ? (
            results.map((r, i) => {
              const Icon = ICONS[r.type] || FileText;
              return (
                <button key={i} onClick={() => handleSelect(r)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition text-left border-b border-slate-50 last:border-0"
                  data-testid={`search-result-${i}`}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${COLORS[r.type] || 'bg-slate-100'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">{r.title}</p>
                    <p className="text-xs text-slate-500 truncate">{r.subtitle} {r.date && `| ${r.date}`}</p>
                  </div>
                  {r.amount > 0 && <span className="font-mono text-sm text-slate-700 shrink-0">Rs.{formatCurrency(r.amount)}</span>}
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${COLORS[r.type] || 'bg-slate-100 text-slate-600'}`}>{r.type}</span>
                </button>
              );
            })
          ) : (
            <div className="text-center py-4 text-sm text-slate-400">No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
